import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import i18n from '../../../i18n';
import { Certificate } from '../../../types/rest/assetCatalogTypes';
import CertificatesTable from './CertificatesTable';
import { confirm } from '../../../components/ui/modal/BlazeConfirmationModal';
import acApi from '../../../axios/ac/acApi';
import { useIsMount } from '../../../hooks/useIsMountHook';
import { cacheControlHeaders, getErrorMessage, getSelectedCountTitle } from 'utils/miscUtil';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import MixpanelService from 'utils/mixpanelService';
import { ASSET_FILES_BULK_OPERATION, DELETE_ASSET, OperationStatus } from 'utils/mixpanelEvents';
import { useDispatch, useSelector } from 'react-redux';
import { filterListByKeyword } from '../../../utils/filterUtils';
import { setIsLoadingCertificate } from '../../../store/certificates/certificatesReducer';
import { fetchCertificatesThunk } from '../../../store/certificates/certificatesActions';
import CertificateDetailsModal from './CertificateDetailsModal';
import CommonAssetHeader from '../CommonAssetHeader';

const Certificates = () => {
  const certificates = useSelector((state: any) => state.certificates.certificates);
  const isLoading = useSelector((state: any) => state.certificates.isLoading);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [certificateDetail, setCertificateDetail] = useState<Certificate | null>(null);
  const [showAddCertModal, setShowAddCertModal] = useState(false);

  // Filtered list
  const filteredList = filterListByKeyword(certificates, searchKeyword, ['name']);
  const count = getSelectedCountTitle(filteredList.length, selectedCertificates.length);

  const isMount = useIsMount();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isMount) {
      dispatch(fetchCertificatesThunk(true));
    }
  }, []);

  const onOpenDetail = (cert: Certificate) => {
    setCertificateDetail(cert);
  };

  const onDelete = (cert?: Certificate) => {
    let modalBody;

    if (_.isNil(cert)) {
      modalBody = (
        <div>{i18n.t('certificates:actions.deleteBulk', { numberOfSelected: selectedCertificates.length })}</div>
      );
    } else {
      modalBody = (
        <div>
          {i18n.t('certificates:actions.delete')} <strong className="text-break">{cert.name}</strong>?
        </div>
      );
    }

    confirm({
      confirmation: modalBody,
      yesOptionTitle: i18n.t('common:actions.delete'),
      title: i18n.t('certificates:actions.deleteTitle'),
    }).then(async () => {
      dispatch(setIsLoadingCertificate(true));
      try {
        if (_.isNil(cert)) {
          const ids: number[] = selectedCertificates.map(crt => crt.id);
          await acApi.assets.deleteBulk(ids);
          MixpanelService.track(ASSET_FILES_BULK_OPERATION('delete', ids.length));
          dispatch(showToastNotification(ToastType.Success, i18n.t('certificates:actions.deleteBulkSuccess')));
        } else {
          const response = await acApi.assets.delete(cert.id, cacheControlHeaders);
          if (response.status === 204)
            dispatch(
              showToastNotification(
                ToastType.Success,
                i18n.t('certificates:actions.deleteSingleSuccess', { name: cert.name }),
              ),
            );
          MixpanelService.track(DELETE_ASSET(cert.name, OperationStatus.SUCCESS, 'CERTIFICATE'));
        }
        dispatch(fetchCertificatesThunk(true, false));
      } catch (err) {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('certificates:errorMsgs.failedToDelete'),
            getErrorMessage(err),
          ),
        );
      } finally {
        clearTableSelection();
      }
    });
  };

  const isDeleteDisabled = () => {
    return _.isEmpty(selectedCertificates);
  };

  const onCloseModal = () => {
    setShowAddCertModal(false);
    setCertificateDetail(null);
  };

  const clearTableSelection = () => {
    setSelectedCertificates([]);
  };

  const header = (
    <CommonAssetHeader
      assetType={'CERTIFICATE'}
      handleCreateNewAsset={() => setShowAddCertModal(true)}
      searchKeyword={searchKeyword}
      selectedAssets={selectedCertificates}
      clearTableSelection={clearTableSelection}
      setSearchKeyword={setSearchKeyword}
      count={count}
      isDeleteDisabled={isDeleteDisabled}
      onDelete={onDelete}
    />
  );

  return (
    <div className={'card'}>
      {(showAddCertModal || certificateDetail) && (
        <CertificateDetailsModal onClose={onCloseModal} fileName={certificateDetail?.name} cert={certificateDetail} />
      )}
      <div className={'card-body'}>
        {header}

        <CertificatesTable
          isLoading={isLoading}
          onOpenDetail={onOpenDetail}
          onSelectionChange={selectedItems => setSelectedCertificates(selectedItems)}
          certificates={filteredList}
          onDelete={onDelete}
          selectedItems={selectedCertificates.map(item => item.id)}
        />
      </div>
    </div>
  );
};

export default Certificates;
